import type { RegionRequest, StopPreview } from '../shared/types.js';

/**
 * Regions the host drew, kept in this browser so a favourite is one click in the lobby
 * rather than a box and a pin redrawn every evening.
 *
 * Local rather than on the server, and with no account: a drawing is a convenience for the
 * person who made it, like the chase view or the volume, and an account is for things other
 * people see — tours and results. A region worth sharing is what a tour is for.
 */
export interface SavedRegion {
  id: string;
  title: string;
  region: RegionRequest;
  /** The street sketch from the wizard's dry run, for the card. Absent if it never ran. */
  preview?: StopPreview;
  savedAt: number;
}

const KEY = 'osmRacer.savedRegions';
/** Enough to keep a handful of favourites; the oldest goes when a new one arrives. */
const MAX_SAVED = 16;

export function loadSavedRegions(): SavedRegion[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is SavedRegion =>
        Boolean(r) && typeof r.id === 'string' && typeof r.title === 'string' && Boolean(r.region?.bbox),
    );
  } catch {
    // Storage off, or a value that is not ours: no saved regions, which is the first visit.
    return [];
  }
}

function store(regions: SavedRegion[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(regions));
  } catch {
    // Full, or off. The region still applies to this lobby; it is just not remembered.
  }
}

/**
 * Keeps a drawn region, newest first. Drawing the same box again replaces the old entry
 * rather than adding a twin — adjusting the start of a saved region is editing it.
 */
export function saveRegion(title: string, region: RegionRequest, preview?: StopPreview): SavedRegion[] {
  const box = boxKey(region);
  const saved: SavedRegion = {
    id: `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    title,
    region,
    ...(preview ? { preview } : {}),
    savedAt: Date.now(),
  };
  const regions = [saved, ...loadSavedRegions().filter((r) => boxKey(r.region) !== box)].slice(0, MAX_SAVED);
  store(regions);
  return regions;
}

export function forgetRegion(id: string): SavedRegion[] {
  const regions = loadSavedRegions().filter((r) => r.id !== id);
  store(regions);
  return regions;
}

function boxKey(region: RegionRequest): string {
  const b = region.bbox;
  return [b.south, b.west, b.north, b.east].map((v) => v.toFixed(4)).join(',');
}

/**
 * Whether the lobby's region is this one — the same box and the same start, which is what
 * the card stands for. The finish is left out: a free drive drops it, and the card should
 * still light up for the region it came from.
 */
export function sameRegion(a: RegionRequest | null | undefined, b: RegionRequest | null | undefined): boolean {
  if (!a || !b) return false;
  if (a.fixture || b.fixture) return a.fixture === b.fixture && sameStart(a, b);
  return boxKey(a) === boxKey(b) && sameStart(a, b);
}

function sameStart(a: RegionRequest, b: RegionRequest): boolean {
  if (!a.start || !b.start) return !a.start && !b.start;
  return Math.abs(a.start.lat - b.start.lat) < 1e-5 && Math.abs(a.start.lon - b.start.lon) < 1e-5;
}
